<script setup lang="ts">
import { useMutation } from "@tanstack/vue-query";
import { type MessageReactive, useMessage } from "naive-ui";
import { computed, onUnmounted, ref } from "vue";
import type { OpenItemComment } from "../../models/contador";
import {
    deleteOpenItemComment,
    updateOpenItemComment,
} from "../../services/contador/open-items.service";
import CommentBubble from "../CommentBubble.vue";
import { getUserEmail } from "../../config/firebase.ts";

const message = useMessage();

const props = defineProps<{
    comment: OpenItemComment;
    showResolvedTag?: boolean;
}>();

const emit = defineEmits<{
    "comment-edited": [];
}>();

const isEditing = ref(false);

let loadingMessage: MessageReactive;
onUnmounted(() => {
    if (loadingMessage) {
        loadingMessage.destroy();
    }
});

const isCurrentUserAuthor = computed(() => {
    return props.comment.author === getUserEmail();
});

const { mutate: updateComment, isPending: isUploading } = useMutation({
    mutationFn: (payload: { content: string }) => {
        loadingMessage = message.loading("Updating comment...", {
            duration: 0,
        });
        return updateOpenItemComment(props.comment.id!, {
            content: payload.content,
            author: props.comment.author,
            open_item_id: props.comment.open_item_id,
        });
    },
    onSuccess() {
        loadingMessage.destroy();
        message.success("Comment updated successfully");
        isEditing.value = false;
        emit("comment-edited");
    },
    onError(error) {
        loadingMessage.destroy();
        console.error("error updating comment", error);
        message.error(`Error updating comment: ${error}`);
    },
});

const { mutate: deleteComment, isPending: isDeleting } = useMutation({
    mutationFn: () => {
        loadingMessage = message.loading("Deleting comment...", {
            duration: 0,
        });
        return deleteOpenItemComment(props.comment.id!);
    },
    onSuccess() {
        loadingMessage.destroy();
        message.success("Comment deleted successfully");
        emit("comment-edited");
    },
    onError(error) {
        loadingMessage.destroy();
        console.error("error deleting comment", error);
        message.error(`Error deleting comment: ${error}`);
    },
});

function onEdit() {
    isEditing.value = true;
}

function onCancel() {
    isEditing.value = false;
}

function onSave(text: string) {
    updateComment({ content: text });
}

const resolvedTag = computed(() => {
    if (!props.showResolvedTag) return undefined;
    return {
        type: "success" as "default" | "primary" | "info" | "success" | "warning" | "error",
        text: "resolved",
        class: "text-label",
    };
});
</script>

<template>
    <comment-bubble
        :comment="comment"
        :can-edit="isCurrentUserAuthor"
        :can-delete="isCurrentUserAuthor"
        :is-editing="isEditing"
        :is-uploading="isUploading"
        :is-deleting="isDeleting"
        :mention-options="[]"
        :tag="resolvedTag"
        attachment-component="div"
        @edit="onEdit"
        @cancel="onCancel"
        @save="onSave"
        @delete="deleteComment"
    />
</template>
